import { ReactNode, useState } from "react";
import { ChevronDown, History, MessageSquare } from "lucide-react";

import { FindingHistoryEntry, FindingHistoryFieldChange, formatReviewDateTime } from "utils/plan-review-finding-review";

// A change whose values are both shorter than this is shown on one line ("Status  Fail → Pass").
// Longer values, like an observation, are shown as a "Previous" and an "Updated" block.
const SHORT_CHANGE_MAX_LENGTH = 32;

interface PlanReviewFindingHistoryProps {
	entries: FindingHistoryEntry[];
	/** Shown on the right of the toggle, for example the "View Full Details" button of a card. */
	rightAction?: ReactNode;
}

const FieldChange = ({ change }: { change: FindingHistoryFieldChange }) => {
	const isShortChange = change.before.length < SHORT_CHANGE_MAX_LENGTH && change.after.length < SHORT_CHANGE_MAX_LENGTH;

	if (isShortChange) {
		return (
			<div className="prfh-change-short">
				<span className="prfh-change-label">{change.label}</span>
				<span className="prfh-change-before">{change.before}</span>
				<span className="prfh-change-arrow">changed to</span>
				<span className="prfh-change-after">{change.after}</span>
			</div>
		);
	}

	return (
		<div className="prfh-change-long">
			<div className="prfh-change-long-title">{change.label} updated</div>
			<div className="prfh-change-previous">
				<span className="prfh-change-previous-label">Previous</span>
				<span className="prfh-change-previous-text">{change.before}</span>
			</div>
			<div className="prfh-change-updated">
				<span className="prfh-change-updated-label">Updated</span>
				<span className="prfh-change-updated-text">{change.after}</span>
			</div>
		</div>
	);
};

/**
 * Collapsible change history of a plan review finding: Ollie's analysis first, then every reviewer
 * change and every feedback, oldest first. Nothing is rendered when Ollie's analysis is the only
 * entry, except the right action.
 */
const PlanReviewFindingHistory = ({ entries, rightAction }: PlanReviewFindingHistoryProps) => {
	const [isOpen, setIsOpen] = useState(false);

	if (entries.length <= 1) {
		return rightAction ? <div className="prfh-actions-only">{rightAction}</div> : null;
	}

	return (
		<div className="prfh" onClick={event => event.stopPropagation()}>
			<div className="prfh-head">
				<button type="button" className="prfh-toggle" onClick={() => setIsOpen(open => !open)} aria-expanded={isOpen}>
					<History size={14} />
					Change history ({entries.length} {entries.length === 1 ? "record" : "records"})
					<ChevronDown size={14} className={`prfh-toggle-chevron${isOpen ? " prfh-toggle-chevron-open" : ""}`} />
				</button>
				{rightAction}
			</div>

			{isOpen && (
				<div className="prfh-list">
					{entries.map((entry, index) => (
						<div key={entry.key} className="prfh-entry">
							<div className="prfh-entry-head">
								<span className="prfh-entry-author">
									<span className="prfh-entry-number">{index + 1}</span>
									<span>
										{entry.name}
										{entry.organization && <span className="prfh-entry-organization"> ({entry.organization})</span>}
									</span>
									<span className="prfh-entry-role">{entry.role}</span>
								</span>
								{entry.at && <span className="prfh-entry-date">{formatReviewDateTime(entry.at)}</span>}
							</div>
							<div className="prfh-entry-action">{entry.action}</div>
							{(entry.fieldChanges.length > 0 || entry.notes.length > 0) && (
								<div className="prfh-entry-changes">
									{entry.fieldChanges.map(change => (
										<FieldChange key={change.label} change={change} />
									))}
									{entry.notes.map(note => (
										<div key={note} className="prfh-note">
											{note}
										</div>
									))}
								</div>
							)}
							{entry.reason && (
								<div className="prfh-reason">
									<MessageSquare size={14} className="prfh-reason-icon" />
									<div>
										<span className="prfh-reason-label">Reason / comment: </span>“{entry.reason}”
									</div>
								</div>
							)}
						</div>
					))}
				</div>
			)}
		</div>
	);
};

export default PlanReviewFindingHistory;
